import numeral from 'numeral';
import { isFinite } from 'lodash';

const PRICE_LOW_THRESHOLD = 0.01;

export const DEFAULT_PRICE_UNIT = 'm';

export const DEFAULT_PRICE_HIDE_DECIMALS = false;

export const UNIT_MAP = {
	k: 1000,
	m: 1000000,
	b: 1000000000,
};

export const DENOMINATION_UNITS = {
	1000: 'k',
	1000000: 'm',
	1000000000: 'b',
	1: '',
};

export const CURRENCIES_SIGNS = {
	usd: '$',
	gbp: '£',
	eur: '€',
};

export const UNITS = Object.keys(UNIT_MAP);

export function percent(total, value) {
	if (total === 0) return 0;

	if (!isFinite(total) || !isFinite(value)) {
		throw new Error('Invalid argument');
	}

	return 100 * (value / total);
}

function getLeadingDecimalZeros(value) {
	return value.toString().replace(/0\.(0+?)[^0]+.*$/g, '$1').length;
}

function getFormatForUnitThousand(value) {
	let decimals = '00';

	if (value < PRICE_LOW_THRESHOLD) {
		const leadingDecimalZeros = getLeadingDecimalZeros(value);
		const totalDecimals = leadingDecimalZeros + 1;
		decimals = Array(totalDecimals + 1).join('0');
	}

	return `0,0.${decimals}ak`;
}

export function getFormattedPrice(
	value,
	unit,
	hideDecimals = DEFAULT_PRICE_HIDE_DECIMALS
) {
	const unitFormat = UNITS.includes(unit) ? unit : DEFAULT_PRICE_UNIT;
	const unitFraction = UNIT_MAP[unitFormat];
	let absoluteValue = Math.abs(value);
	const calculatedValue = absoluteValue / unitFraction;
	const decimalFormat = hideDecimals ? '' : '.00';

	let format = `0,0${decimalFormat}a${unitFormat}`;

	if (unitFormat === 'k' && !hideDecimals) {
		format = getFormatForUnitThousand(calculatedValue);
	} else if (calculatedValue < PRICE_LOW_THRESHOLD) {
		// We need to change the value otherwise numeral will return NaN
		// as the value will be converted to exponential notation
		absoluteValue = 0;
	}

	return numeral(absoluteValue).format(format);
}

export const getIdealDenomination = (value, isShowInThousnads) => {
	const candidates =
		isShowInThousnads === true
			? [UNIT_MAP.k, 1]
			: [UNIT_MAP.b, UNIT_MAP.m, UNIT_MAP.k, 1];
	const denomination = candidates.find(
		candidate => Math.abs(value) / candidate >= 1
	);
	return denomination || 1;
};

export const getFormattedSpend = (value, currency) => {
	const denomination = getIdealDenomination(value);
	const displayValue = value / denomination;
	const decimal = displayValue > 100 ? '0' : '00';
	const formattedStr = numeral(value / denomination).format(`0,0[.]${decimal}`);
	return `${CURRENCIES_SIGNS[currency]}${formattedStr}${
		DENOMINATION_UNITS[denomination]
	}`;
};
export const formatCurrencyWithSuffix = number => {
	const suffixes = ['', 'K', 'M', 'B', 'T', 'Q'];
	const tier = (Math.log10(Math.abs(number)) / 3) | 0;

	if (tier === 0) return `$${number.toFixed(1)}`;

	const suffix = suffixes[tier];
	const scale = Math.pow(10, tier * 3);
	const scaledNumber = number / scale;

	return `$${scaledNumber.toFixed(1)}${suffix}`;
};

export const getFormattedSpendWithSymbol = (
	value,
	currencySymbol,
	isDecimalEnable,
	isShowInThousnads = ''
) => {
	const denomination = getIdealDenomination(value, isShowInThousnads);
	let displayValue = Math.abs(value / denomination);
	if (isDecimalEnable) {
		displayValue = Math.round(displayValue);
	}
	const decimal = displayValue > 100 ? '0' : '00';
	const formattedStr = numeral(displayValue).format(`0,0[.]${decimal}`);
	const negativePrefix = value < 0 ? '-' : '';

	return `${negativePrefix}${currencySymbol}${formattedStr}${
		DENOMINATION_UNITS[denomination]
	}`;
};

export const getFormattedPercentage = value =>
	value === null || value === undefined
		? '-'
		: numeral(Math.abs(value) / 100).format('0,0.[00]%');

export function padExtent(extent, percentage) {
	const [min, max] = extent;
	const range = max - min || max;
	const padding = range * percentage;
	let paddedMin = min - padding;
	const paddedMax = max + padding;
	// EDGE CASES
	// if min valye is zero, then prefer zero
	if (min === 0) {
		paddedMin = 0;
	}
	// if min value is higher than zero but padded min goes below zero, then prefer zero
	if (min > 0 && paddedMin < 0) {
		paddedMin = 0;
	}
	return [paddedMin, paddedMax];
}

export function convertValueToUnitPair(value, name) {
	const valuePropName = name + 'Value';
	const unitPropName = name + 'Unit';
	// Nine Zeroes for Billions
	return Math.abs(Number(value)) >= 1.0e9
		? {
				[valuePropName]: Math.abs(Number(value)) / 1.0e9,
				[unitPropName]: 'b',
		  }
		: // Six Zeroes for Millions
		  Math.abs(Number(value)) >= 1.0e6
			? {
					[valuePropName]: Math.abs(Number(value)) / 1.0e6,
					[unitPropName]: 'm',
			  }
			: {
					[valuePropName]: Math.abs(Number(value)) / 1.0e3,
					[unitPropName]: 'k',
			  };
}

export function formatNumber(num) {
	return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
}

// moved from TSXR
export const getFormattedCurrencyWithIdealDenomination = ({
	currencyFormat = {},
	value,
	isCurrencySign = false,
	isUnitSign = false,
	hideDecimals,
}) => {
	const denomination = getIdealDenomination(value);
	let displayValue = value / denomination;
	if (hideDecimals) {
		displayValue = Math.round(displayValue);
	}
	const decimal = displayValue > 100 ? '0' : '00';
	const formattedStr = numeral(displayValue).format(`0,0[.]${decimal}`);
	const unitSign = isUnitSign ? IDEAL_UNITS_SIGNS[denomination] : '';
	const { symbol, currency } = currencyFormat;
	const currencySign = isCurrencySign
		? `${symbol || CURRENCIES_SIGNS[currency] || currency}`
		: '';
	return `${currencySign}${formattedStr}${unitSign}`;
};

const BILLION = 1000000000;
const MILLION = 1000000;
const THOUSAND = 1000;

export const IDEAL_UNITS_SIGNS = {
	[THOUSAND]: 'K',
	[MILLION]: 'M',
	[BILLION]: 'B',
	1: '',
};

export const formatSpendWithDecimalAndSymbol = (
	value,
	currencySymbol,
	isDecimalEnable,
	decimalCount = 1,
	isShowInThousnads = ''
) => {
	const denomination = getIdealDenomination(value, isShowInThousnads);
	let displayValue = Math.abs(value / denomination);

	if (isDecimalEnable) {
		displayValue = Math.round(displayValue);
	}

	let formatString;
	if (isDecimalEnable) {
		formatString = '0,0'; // no decimals if rounding is enabled
	} else {
		formatString = `0,0.${'0'.repeat(decimalCount)}`;
	}

	const formattedStr = numeral(displayValue).format(formatString);
	const negativePrefix = value < 0 ? '-' : '';

	return `${negativePrefix}${currencySymbol}${formattedStr}${DENOMINATION_UNITS[
		denomination
	] || ''}`;
};

export function formatNumberWithDecimals(value, isDecimalEnable) {
	if (isDecimalEnable) {
		return Math.trunc(value);
	}
	return parseFloat(value).toFixed(2);
}
